<template>
  <div class="interactive-chart-container">
    <div class="chart-header">
      <div class="chart-title-section">
        <h6 class="chart-title">{{ title }}</h6>
        <p class="chart-subtitle" v-if="subtitle">{{ subtitle }}</p>
      </div>
      <div class="chart-controls">
        <!-- Selector de tipo de gráfico -->
        <el-dropdown @command="changeChartType" trigger="click">
          <el-button size="small">
            <i :class="currentChartIcon" class="me-1"></i>
            {{ currentChartLabel }}
            <i class="ri-arrow-down-s-line ms-1"></i>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item 
                v-for="type in chartTypes"
                :key="type.value"
                :command="type.value"
                :class="{ 'is-active': type.value === chartType }"
              >
                <i :class="type.icon" class="me-2"></i>
                {{ type.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        
        <!-- Controles adicionales -->
        <el-button-group size="small">
          <el-tooltip content="Pantalla completa" placement="top">
            <el-button @click="toggleFullscreen">
              <i class="ri-fullscreen-line"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip content="Descargar" placement="top">
            <el-button @click="downloadChart">
              <i class="ri-download-line"></i>
            </el-button>
          </el-tooltip>
          <el-tooltip content="Actualizar" placement="top">
            <el-button @click="refreshChart" :loading="isRefreshing">
              <i class="ri-refresh-line"></i>
            </el-button>
          </el-tooltip>
        </el-button-group>
      </div>
    </div>
    
    <div 
      class="chart-content" 
      :class="{ 'fullscreen': isFullscreen }"
      ref="chartContainer"
    >
      <div v-if="isLoading" class="chart-loading">
        <div class="loading-spinner">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2">Cargando datos del gráfico...</p>
        </div>
      </div>
      
      <div v-else-if="hasError" class="chart-error">
        <i class="ri-error-warning-line"></i>
        <p>Error al cargar los datos del gráfico</p>
        <el-button size="small" @click="refreshChart">Reintentar</el-button>
      </div>
      
      <canvas 
        v-else
        ref="chartCanvas"
        :id="chartId"
        class="interactive-chart-canvas"
        @click="onChartClick"
        @mousemove="onChartHover"
      ></canvas>
      
      <!-- Leyenda personalizada -->
      <div v-if="showLegend && chartInstance" class="custom-legend">
        <div 
          v-for="(item, index) in legendItems"
          :key="index"
          class="legend-item"
          :class="{ 'legend-item-hidden': item.hidden }"
          @click="toggleLegendItem(index)"
        >
          <span 
            class="legend-color-box"
            :style="{ backgroundColor: item.color }"
          ></span>
          <span class="legend-text">{{ item.text }}</span>
          <span class="legend-value">{{ item.value }}</span>
        </div>
      </div>
    </div>
    
    <!-- Panel de información del punto seleccionado -->
    <div v-if="selectedPoint" class="point-info-panel">
      <div class="point-info-header">
        <h6>Información del Punto</h6>
        <el-button size="small" text @click="selectedPoint = null">
          <i class="ri-close-line"></i>
        </el-button>
      </div>
      <div class="point-info-content">
        <div class="point-info-item" v-for="(value, key) in selectedPoint" :key="key">
          <span class="point-info-label">{{ key }}:</span>
          <span class="point-info-value">{{ value }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'
import html2canvas from 'html2canvas'

// Registrar todos los elementos de Chart.js
Chart.register(...registerables)

export default {
  name: 'InteractiveChart',
  props: {
    title: {
      type: String,
      default: 'Gráfico Interactivo'
    },
    subtitle: {
      type: String,
      default: ''
    },
    chartId: {
      type: String,
      default: () => `chart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    },
    chartType: {
      type: String,
      default: 'doughnut',
      validator: (value) => ['doughnut', 'bar', 'line', 'radar', 'polarArea', 'scatter'].includes(value)
    },
    data: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    interactive: {
      type: Boolean,
      default: true
    }
  },
  emits: ['chart-click', 'chart-hover', 'chart-refresh', 'type-changed'],
  setup(props, { emit }) {
    const chartCanvas = ref(null)
    const chartContainer = ref(null)
    const chartInstance = ref(null)
    const isLoading = ref(false)
    const isRefreshing = ref(false)
    const hasError = ref(false)
    const isFullscreen = ref(false)
    const selectedPoint = ref(null)
    
    // Tipos de gráficos disponibles
    const chartTypes = [
      { value: 'doughnut', label: 'Circular', icon: 'ri-pie-chart-line' },
      { value: 'bar', label: 'Barras', icon: 'ri-bar-chart-grouped-line' },
      { value: 'line', label: 'Líneas', icon: 'ri-line-chart-line' },
      { value: 'radar', label: 'Radar', icon: 'ri-radar-line' },
      { value: 'polarArea', label: 'Área Polar', icon: 'ri-donut-chart-line' },
      { value: 'scatter', label: 'Dispersión', icon: 'ri-scatter-chart-line' }
    ]
    
    // Tipo de gráfico actual
    const currentChartType = ref(props.chartType)
    
    const currentChartLabel = computed(() => {
      const type = chartTypes.find(t => t.value === currentChartType.value)
      return type ? type.label : 'Gráfico'
    })
    
    const currentChartIcon = computed(() => {
      const type = chartTypes.find(t => t.value === currentChartType.value)
      return type ? type.icon : 'ri-bar-chart-line'
    })
    
    // Items de la leyenda
    const legendItems = computed(() => {
      if (!chartInstance.value || !props.data.datasets) return []
      
      const dataset = props.data.datasets[0]
      if (!dataset) return []
      
      return props.data.labels.map((label, index) => ({
        text: label,
        color: dataset.backgroundColor?.[index] || '#4361ee',
        value: dataset.data?.[index] || 0,
        hidden: chartInstance.value.isDatasetVisible(0) && 
               chartInstance.value.getDataVisibility ? 
               !chartInstance.value.getDataVisibility(index) : false
      }))
    })
    
    // Opciones por defecto para diferentes tipos de gráficos
    const getDefaultOptions = (type) => {
      const baseOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: type === 'doughnut' || type === 'polarArea' ? 1 : 2,
        plugins: {
          legend: {
            display: false // Usamos leyenda personalizada
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(20, 20, 20, 0.9)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              title: function(context) {
                return context[0].label || ''
              },
              label: function(context) {
                const value = context.raw || context.parsed.y || context.parsed
                const total = context.dataset.data.reduce((a, b) => a + b, 0)
                const percentage = Math.round((value / total) * 100)
                return `${context.dataset.label || ''}: ${value} (${percentage}%)`
              }
            }
          },
          datalabels: {
            display: type === 'doughnut' || type === 'polarArea',
            color: '#ffffff',
            font: {
              weight: 'bold',
              size: 12
            },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const percentage = Math.round((value / total) * 100)
              return percentage > 5 ? `${percentage}%` : ''
            }
          }
        },
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        },
        interaction: {
          intersect: props.interactive,
          mode: 'nearest'
        }
      }
      
      // Opciones específicas por tipo
      switch (type) {
        case 'doughnut':
          return {
            ...baseOptions,
            cutout: '60%',
            radius: '90%'
          }
        case 'bar':
          return {
            ...baseOptions,
            scales: {
              x: {
                grid: {
                  display: false
                },
                ticks: {
                  maxRotation: 45,
                  minRotation: 0
                }
              },
              y: {
                beginAtZero: true,
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }
          }
        case 'line':
          return {
            ...baseOptions,
            elements: {
              line: {
                tension: 0.4,
                borderWidth: 3
              },
              point: {
                radius: 6,
                hoverRadius: 8
              }
            },
            scales: {
              x: {
                grid: {
                  display: false
                }
              },
              y: {
                beginAtZero: true,
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }
          }
        default:
          return baseOptions
      }
    }
    
    // Crear o actualizar gráfico
    const createChart = () => {
      if (!chartCanvas.value) return
      
      // Destruir gráfico existente
      if (chartInstance.value) {
        chartInstance.value.destroy()
      }
      
      const ctx = chartCanvas.value.getContext('2d')
      const mergedOptions = {
        ...getDefaultOptions(currentChartType.value),
        ...props.options
      }
      
      try {
        chartInstance.value = new Chart(ctx, {
          type: currentChartType.value,
          data: props.data,
          options: mergedOptions
        })
        
        hasError.value = false
      } catch (error) {
        console.error('Error creating chart:', error)
        hasError.value = true
      }
    }
    
    // Cambiar tipo de gráfico
    const changeChartType = (newType) => {
      currentChartType.value = newType
      emit('type-changed', newType)
      nextTick(() => {
        createChart()
      })
    }
    
    // Toggle pantalla completa
    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value
      
      if (isFullscreen.value) {
        chartContainer.value?.requestFullscreen?.()
      } else {
        document.exitFullscreen?.()
      }
    }
    
    // Descargar gráfico
    const downloadChart = async () => {
      if (!chartContainer.value) return
      
      try {
        const canvas = await html2canvas(chartContainer.value)
        const link = document.createElement('a')
        link.download = `${props.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
      } catch (error) {
        console.error('Error downloading chart:', error)
      }
    }
    
    // Actualizar gráfico
    const refreshChart = async () => {
      isRefreshing.value = true
      emit('chart-refresh')
      
      // Simular carga
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (chartInstance.value) {
        chartInstance.value.update()
      }
      
      isRefreshing.value = false
    }
    
    // Manejar click en gráfico
    const onChartClick = (event) => {
      if (!props.interactive || !chartInstance.value) return
      
      const points = chartInstance.value.getElementsAtEventForMode(
        event,
        'nearest',
        { intersect: true },
        true
      )
      
      if (points.length) {
        const point = points[0]
        const data = {
          datasetIndex: point.datasetIndex,
          index: point.index,
          label: props.data.labels[point.index],
          value: props.data.datasets[point.datasetIndex].data[point.index]
        }
        
        selectedPoint.value = data
        emit('chart-click', data)
      }
    }
    
    // Manejar hover en gráfico
    const onChartHover = (event) => {
      if (!props.interactive || !chartInstance.value) return
      
      const points = chartInstance.value.getElementsAtEventForMode(
        event,
        'nearest',
        { intersect: true },
        true
      )
      
      if (points.length) {
        const point = points[0]
        const data = {
          datasetIndex: point.datasetIndex,
          index: point.index,
          label: props.data.labels[point.index],
          value: props.data.datasets[point.datasetIndex].data[point.index]
        }
        
        emit('chart-hover', data)
      }
    }
    
    // Toggle item de leyenda
    const toggleLegendItem = (index) => {
      if (!chartInstance.value) return
      
      if (chartInstance.value.toggleDataVisibility) {
        chartInstance.value.toggleDataVisibility(index)
      } else {
        // Fallback para versiones más antiguas
        const meta = chartInstance.value.getDatasetMeta(0)
        meta.data[index].hidden = !meta.data[index].hidden
      }
      
      chartInstance.value.update()
    }
    
    // Watchers
    watch(() => props.data, () => {
      if (chartInstance.value) {
        chartInstance.value.data = props.data
        chartInstance.value.update()
      }
    }, { deep: true })
    
    watch(() => props.options, () => {
      createChart()
    }, { deep: true })
    
    // Lifecycle
    onMounted(() => {
      nextTick(() => {
        createChart()
      })
    })
    
    onUnmounted(() => {
      if (chartInstance.value) {
        chartInstance.value.destroy()
      }
    })
    
    return {
      chartCanvas,
      chartContainer,
      chartInstance,
      isLoading,
      isRefreshing,
      hasError,
      isFullscreen,
      selectedPoint,
      chartTypes,
      currentChartType,
      currentChartLabel,
      currentChartIcon,
      legendItems,
      changeChartType,
      toggleFullscreen,
      downloadChart,
      refreshChart,
      onChartClick,
      onChartHover,
      toggleLegendItem
    }
  }
}
</script>

<style scoped>
.interactive-chart-container {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.interactive-chart-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(255, 255, 255, 0.25);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.chart-title-section .chart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: inherit;
}

.chart-title-section .chart-subtitle {
  margin: 4px 0 0 0;
  font-size: 12px;
  opacity: 0.7;
  color: inherit;
}

.chart-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.chart-content {
  position: relative;
  padding: 20px;
  height: 350px;
  display: flex;
  flex-direction: column;
}

.chart-content.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 40px;
  height: 100vh;
}

.interactive-chart-canvas {
  width: 100% !important;
  height: 280px !important;
  max-height: 280px;
  cursor: pointer;
  display: block;
}

.chart-loading,
.chart-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 250px;
  color: inherit;
}

.chart-error i {
  font-size: 48px;
  color: #f56565;
  margin-bottom: 16px;
}

.loading-spinner {
  text-align: center;
}

.custom-legend {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.legend-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.legend-item-hidden {
  opacity: 0.5;
}

.legend-color-box {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

.legend-text {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
}

.legend-value {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.8;
}

.point-info-panel {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px;
  min-width: 200px;
  z-index: 100;
}

.point-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.point-info-header h6 {
  margin: 0;
  color: #ffffff;
  font-size: 14px;
}

.point-info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.point-info-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.point-info-value {
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
}

/* Dark mode styles */
body.dark-mode-active .interactive-chart-container {
  background: rgba(26, 32, 44, 0.6);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .interactive-chart-container:hover {
  border-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode-active .chart-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .chart-title-section .chart-title {
  color: rgba(255, 255, 255, 0.9);
}

body.dark-mode-active .chart-title-section .chart-subtitle {
  color: rgba(255, 255, 255, 0.6);
}

body.dark-mode-active .custom-legend {
  border-top-color: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .legend-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

body.dark-mode-active .legend-text,
body.dark-mode-active .legend-value {
  color: rgba(255, 255, 255, 0.8);
}

/* Responsive */
@media (max-width: 992px) {
  .chart-content {
    height: 320px;
  }
  
  .interactive-chart-canvas {
    height: 250px !important;
    max-height: 250px;
  }
}

@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .chart-controls {
    justify-content: center;
  }
  
  .chart-content {
    padding: 16px;
    height: 280px;
  }
  
  .interactive-chart-canvas {
    height: 220px !important;
    max-height: 220px;
  }
  
  .custom-legend {
    grid-template-columns: 1fr;
    margin-top: 12px;
  }
  
  .point-info-panel {
    position: relative;
    top: auto;
    right: auto;
    margin-top: 16px;
  }
}

@media (max-width: 480px) {
  .chart-content {
    padding: 12px;
    height: 240px;
  }
  
  .interactive-chart-canvas {
    height: 180px !important;
    max-height: 180px;
  }
  
  .custom-legend {
    margin-top: 8px;
  }
}
</style>